// i18next-extract-mark-ns-start law-changes

import React, { FC, useContext, useEffect, useState } from 'react'
import LawChangesList from 'src/components/law-changes/LawChangesList'
import { I18nextContext, useTranslation } from 'gatsby-plugin-react-i18next'
import RelatedDocuments from 'src/components/document/RelatedDocuments'
import { IRelatedDocument } from 'src/components/document/document-types'
import config from 'src/../tailwind.config'
import Subtitle from 'src/components/typography/Subtitle'
import ThinHeader from 'src/components/layout/ThinHeader'
import IframeLoadingSpinner from '../ui/IframeLoadingSpinner'

export interface LawChangeDetail {
  id: string
  title: string
  hideAmendedTextHeader: boolean
  amendedTextHeader: string
  amendedText: string
  explanation: string
  buttonText?: string
  buttonLink?: string
  swapBlocks: boolean
}

export interface LawChange {
  season: {
    season: string
    slug: string
  }
  id: string
  affectsMultipleLaws: boolean
  changeTitle: string
  changeSummary: string
  changeDetails: LawChangeDetail[]
}

export interface LawChangesData {
  multiLawChanges: LawChange[]
  singleLawChanges: LawChange[]
  relatedDocuments: IRelatedDocument[]
  showPresentation25n26: boolean
  showPresentation26n27: boolean
}

const LawChanges: FC<LawChangesData> = ({
  multiLawChanges,
  singleLawChanges,
  relatedDocuments,
  showPresentation25n26,
  showPresentation26n27,
}) => {
  const { t } = useTranslation('law-changes')
  const { language } = useContext(I18nextContext)
  const [isLoading, setIsLoading] = useState(true)

  const hideLoaderWhenLoaded = () => {
    const presentation = document.getElementById('presentation') as HTMLIFrameElement

    if (!presentation) {
      return
    }

    presentation.addEventListener('load', () => {
      setIsLoading(false)
    })
  }

  useEffect(() => {
    hideLoaderWhenLoaded()
  })

  return (
    <div className="mb-14">
      {showPresentation25n26 && (
        <div>
          <ThinHeader>{t('Full presentation')}</ThinHeader>
          <div
            style={{
              overflow: 'hidden',
              position: 'relative',
              height: 0,
              padding: '1px',
              paddingBottom: '56.25%',
            }}
            className="w-full mb-12"
          >
            <iframe
              id="presentation"
              style={{
                left: 0,
                top: 0,
                height: '100%',
                width: '100%',
                position: 'absolute',
              }}
              title="Law changes presentation"
              src={t('PRESENTATION_PATH_25n26')}
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
              frameBorder="0"
              allow="fullscreen; accelerometer; autoplay; encrypted-media; gyroscope"
            />
            <IframeLoadingSpinner isLoading={isLoading} />
          </div>
        </div>
      )}
      {showPresentation26n27 && (
        <div>
          <ThinHeader>{t('Full presentation')}</ThinHeader>
          <a
              href={t('PRESENTATION_PATH_26n27')}
              target="_blank"
              rel="noreferrer"
              className="w-full block mb-12"
            >
              <img
                src={`/images/law-changes/${language}.jpg`}
                className="w-full"
                alt={t('Open the presentation')}
              />
            </a>
        </div>
      )}
      {multiLawChanges.length ? (
        <LawChangesList
          title={t('Changes Affecting Multiple Laws')}
          lawChanges={multiLawChanges}
          className="mb-24"
        />
      ) : (
        <></>
      )}
      {singleLawChanges.length ? (
        <LawChangesList
          title={t('Individual Law changes')}
          lawChanges={singleLawChanges}
          className="mb-24"
        />
      ) : (
        <></>
      )}
      {relatedDocuments?.length && (
        <>
          <Subtitle style={{ marginBottom: 0 }}>{t('Documents')}</Subtitle>
          <RelatedDocuments
            className="mb-12"
            items={relatedDocuments}
            color={config.theme.extend.colors.navyBlueGray}
            noTopBorder
          />
        </>
      )}
    </div>
  )
}

export default LawChanges
